/**
 * PreLaunchChecklist — the redesigned validator surface. Replaces the
 * old right-rail triad (ValidationGuide + ValidationChecklist +
 * LaunchReadyMessage) with a single gradient-branded card that renders
 * one of three states:
 *
 *   1. Issues found  — hard validation errors. Five category rows show
 *      pass/fail; "Complete N items →" opens the first failed item.
 *      Launch is hard-blocked (lives in StandardFooter, gated upstream).
 *   2. Needs review  — no hard errors, but the 3 manual attestations
 *      (audience rules, sender, preview) aren't all ticked yet. These
 *      are REQUIRED to launch (gate lifted to TemplateEdit).
 *   3. All clear     — clean + all attested. Five green rows.
 *
 * STRICTNESS: like the components it replaces, this must never crash the
 * editor. Validation input is freetext (TemplateValidationError.errors
 * is string[]), so categorization is keyword-based — the same approach
 * the old getHint/getFixHint helpers used. Defensive array/string guards
 * throughout; wrap in PreLaunchChecklistBoundary at the call site.
 */

import styled from '@emotion/styled';
import React, { useEffect, useRef } from 'react';
import type { TemplateValidationError } from '../../../../services/experiences/templateValidation';
import { useAIObservability } from '../../../../services/productAnalytics/aiObservabilityEvents';

const HELP_EMAIL = 'help@dadohr.com';
// Help-article URLs locked in during the ai-brainstorming session.
const HELP_URL_AUDIENCES = 'https://help.dadohr.com/article/31-target-audiences';
const HELP_URL_TESTING =
  'https://help.dadohr.com/article/121-how-to-test-your-dado-experience';

// ─── Attestations ────────────────────────────────────────────────────
export type AttestationKey = 'audiences' | 'sender' | 'preview';
export interface Attestations {
  audiences: boolean;
  sender: boolean;
  preview: boolean;
}

const ATTESTATIONS: {
  key: AttestationKey;
  title: string;
  description: string;
  helpUrl: string;
}[] = [
  {
    key: 'audiences',
    title: 'Audience rules',
    description:
      "Can't be validated automatically — confirm who receives each task.",
    helpUrl: HELP_URL_AUDIENCES,
  },
  {
    key: 'sender',
    title: 'Notification sender',
    description: 'Check the sender name and reply-to in Settings → Notifications.',
    helpUrl: HELP_URL_TESTING,
  },
  {
    key: 'preview',
    title: 'Preview before launch',
    description: 'Enroll a test participant to verify the end-to-end flow.',
    helpUrl: HELP_URL_TESTING,
  },
];

// ─── Category taxonomy ───────────────────────────────────────────────
// The design's five fixed rows. Validation errors (freetext) are mapped
// into these buckets by keyword. A category is "failed" if any error
// maps to it; the first such error supplies the detail + open target.
type CategoryKey = 'structure' | 'actors' | 'content' | 'timing' | 'subtasks';

const CATEGORY_ORDER: { key: CategoryKey; label: string }[] = [
  { key: 'structure', label: 'Experience structure' },
  { key: 'actors', label: 'Actors & assignments' },
  { key: 'content', label: 'Message content' },
  { key: 'timing', label: 'Timing & schedule' },
  { key: 'subtasks', label: 'Subtask completeness' },
];

const categorizeError = (error: string): CategoryKey => {
  const s = error.toLowerCase();
  if (/timing|deadline|start and|schedule|\bdate\b/.test(s)) return 'timing';
  if (/actor|assign|recipient|sender/.test(s)) return 'actors';
  if (/subtask|block|at least one|required field/.test(s)) return 'subtasks';
  if (/subject|link|url|content|message/.test(s)) return 'content';
  return 'structure';
};

interface CategoryRow {
  key: CategoryKey;
  label: string;
  passed: boolean;
  detail?: string;
  itemTitle?: string;
  itemType?: 'TASK' | 'MESSAGE' | 'AUTOMATION';
  itemId?: string;
}

const safeArr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

// Build the 5 fixed category rows from validation errors. First failing
// error per category wins the detail + click target.
const buildCategories = (errors: TemplateValidationError[]): CategoryRow[] => {
  const failed = new Map<CategoryKey, CategoryRow>();
  for (const entry of safeArr<TemplateValidationError>(errors)) {
    if (!entry || typeof entry !== 'object') continue;
    const msgs = safeArr<string>(entry.errors).filter(
      (m): m is string => typeof m === 'string'
    );
    for (const msg of msgs) {
      const cat = categorizeError(msg);
      if (failed.has(cat)) continue; // keep first
      failed.set(cat, {
        key: cat,
        label: CATEGORY_ORDER.find((c) => c.key === cat)?.label ?? cat,
        passed: false,
        detail: msg,
        itemTitle:
          typeof entry.itemTitle === 'string' ? entry.itemTitle : undefined,
        itemType: entry.itemType,
        itemId: typeof entry.itemId === 'string' ? entry.itemId : undefined,
      });
    }
  }
  return CATEGORY_ORDER.map(
    (c) => failed.get(c.key) ?? { key: c.key, label: c.label, passed: true }
  );
};

// ─── Styled ──────────────────────────────────────────────────────────
const Panel = styled('aside')`
  position: fixed;
  top: 64px;
  right: 0;
  width: 360px;
  max-width: 100vw;
  max-height: calc(100vh - 128px);
  overflow-y: auto;
  z-index: 10;
  animation: slideIn 0.2s ease-out;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @keyframes slideIn {
    from {
      transform: translateX(360px);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const Card = styled('div')`
  margin: 16px 20px 16px 0;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow:
    0 2px 10px rgba(8, 18, 26, 0.1),
    inset 0 0 0 1px #ebedef;
`;

const GradientStrip = styled('div')`
  height: 3px;
  background: ${(p) => p.theme.dado.copilot.gradientHorizontal};
`;

const Content = styled('div')`
  padding: 16px 16px 10px;
`;

const HeaderRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const TitleWrap = styled('div')`
  display: flex;
  align-items: center;
  gap: 7px;
`;

// Right side of the header: optional status pill + the close button.
const HeaderActions = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

// Subtle gray icon button — matches the muted palette used across the card
// (#778088 text, #f0f2f4 hover) rather than the gradient/colored accents.
const CloseButton = styled('button')`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #778088;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease, color 120ms ease;
  &:hover {
    background: #f0f2f4;
    color: #272c30;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

const Title = styled('span')`
  font-size: 14px;
  font-weight: 600;
  color: #272c30;
`;

const Pill = styled('div')`
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: ${(p) => p.theme.dado.copilot.blueBanner};
  color: #0052cc;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const ProgressRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const ProgressTrack = styled('div')`
  flex: 1;
  height: 6px;
  background: #ebedef;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled('div')<{ pct: number }>`
  height: 6px;
  width: ${(p) => p.pct}%;
  background: ${(p) => p.theme.dado.copilot.gradientHorizontal};
  border-radius: 999px;
  transition: width 300ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ProgressLabel = styled('div')<{ tone: 'green' | 'blue' }>`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => (p.tone === 'green' ? '#2E8B63' : '#0E78E4')};
`;

const Eyebrow = styled('div')`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #778088;
  margin-bottom: 8px;
`;

const RowList = styled('div')`
  display: flex;
  flex-direction: column;
`;

const CategoryItem = styled('div')<{ clickable?: boolean; last?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 7px 0;
  border-bottom: ${(p) => (p.last ? 'none' : '1px solid #f0f2f4')};
  cursor: ${(p) => (p.clickable ? 'pointer' : 'default')};
  user-select: none;
  &:hover {
    ${(p) => (p.clickable ? 'background: #f0f5fa; margin: 0 -16px; padding: 7px 16px;' : '')}
  }
`;

const PassedText = styled('span')`
  font-size: 13px;
  font-weight: 300;
  color: #494f55;
`;

const FailTitle = styled('div')`
  font-size: 13px;
  font-weight: 600;
  color: #0052cc;
`;

const FailDetail = styled('div')`
  font-size: 12px;
  font-weight: 300;
  color: #778088;
  margin-top: 2px;
  line-height: 1.4;
`;

const AttestRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f4;
  &:last-of-type {
    border-bottom: none;
  }
`;

const AttestBox = styled('button')<{ checked: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
  margin-top: 2px;
  padding: 0;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease;
  background: ${(p) => (p.checked ? '#77D4A9' : 'transparent')};
  box-shadow: ${(p) => (p.checked ? 'none' : 'inset 0 0 0 1.5px #C1C7CD')};
  &:focus-visible {
    outline: none;
    box-shadow:
      ${(p) => (p.checked ? 'none' : 'inset 0 0 0 1.5px #C1C7CD')},
      0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

const AttestTextWrap = styled('div')`
  cursor: pointer;
`;

const AttestTitle = styled('div')`
  font-size: 13px;
  font-weight: 600;
  color: #272c30;
`;

const AttestDesc = styled('div')`
  font-size: 12px;
  font-weight: 300;
  color: #5f676e;
  margin-top: 2px;
  line-height: 1.5;
`;

const AttestHelp = styled('a')`
  color: #0e78e4;
  text-decoration: underline;
  font-size: 12px;
  margin-left: 4px;
`;

const EscapeHatch = styled('p')`
  margin: 14px 0 0;
  font-size: 12px;
  font-weight: 300;
  line-height: 1.55;
  color: #5f676e;
`;

const MailLink = styled('a')`
  color: #0e78e4;
  text-decoration: underline;
  font-weight: 500;
`;

const Footer = styled('div')`
  padding: 11px 16px;
  border-top: 1px solid #ebedef;
  display: flex;
  gap: 8px;
`;

const CompleteButton = styled('button')`
  flex: 1;
  height: 34px;
  border: none;
  border-radius: 6px;
  background: ${(p) => p.theme.dado.copilot.gradientHorizontal};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: filter 120ms ease;
  &:hover {
    filter: brightness(1.05);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

const ReadyNote = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #2e8b63;
`;

// ─── Icons ───────────────────────────────────────────────────────────
const CheckIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2.5 7L5.5 10L11.5 4"
      stroke="#77D4A9"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 14 14"
    fill="none"
    style={{ marginTop: 2, flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="5" stroke="#0E78E4" strokeWidth="1.5" />
    <path d="M7 5V5.5M7 7.5V10" stroke="#0E78E4" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Sparkle: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="#272C30" aria-hidden="true">
    <path d="M6 0L7.2 4.8L12 6L7.2 7.2L6 12L4.8 7.2L0 6L4.8 4.8L6 0Z" />
  </svg>
);

const InfoPillIcon: React.FC = () => (
  <svg width="9" height="9" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="2" />
    <path d="M7 5.5V5.5M7 7.5V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CheckWhite: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2.5 7L5.5 10L11.5 4"
      stroke="#fff"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRight: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2 7H12M8 3L12 7L8 11"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────
export interface PreLaunchChecklistProps {
  errors: TemplateValidationError[];
  attestations: Attestations;
  onToggleAttestation: (key: AttestationKey) => void;
  onOpenItem: (itemType: 'TASK' | 'MESSAGE' | 'AUTOMATION', itemId: string) => void;
  templateId?: string;
  /** When provided, renders a close (×) button that hides the pane. The pane
   *  re-appears the next time the admin runs Check Changes. Optional so the
   *  card can still render standalone (e.g. tests) without a dismiss handler. */
  onClose?: () => void;
}

export const PreLaunchChecklist: React.FC<PreLaunchChecklistProps> = ({
  errors,
  attestations,
  onToggleAttestation,
  onOpenItem,
  templateId,
  onClose,
}) => {
  const aiObs = useAIObservability();
  const safeErrors = safeArr<TemplateValidationError>(errors);
  const hasErrors = safeErrors.length > 0;
  const totalErrors = safeErrors.reduce(
    (sum, e) => sum + (Array.isArray(e?.errors) ? e.errors.length : 0),
    0
  );
  const categories = buildCategories(safeErrors);
  const passedCount = categories.filter((c) => c.passed).length;
  const allAttested =
    attestations.audiences && attestations.sender && attestations.preview;
  const attestRemaining = ATTESTATIONS.filter((a) => !attestations[a.key]).length;

  // Fire LAUNCH_READY_SHOWN once on entry into the clean (no-errors)
  // state — preserves the analytics the old LaunchReadyMessage emitted.
  const readyShownRef = useRef(false);
  useEffect(() => {
    if (hasErrors) {
      readyShownRef.current = false;
      return;
    }
    if (readyShownRef.current || !templateId) return;
    readyShownRef.current = true;
    aiObs.trackLaunchReadyShown(templateId);
  }, [hasErrors, templateId, aiObs]);

  const onHelpEmailClick = (): void => {
    if (templateId) aiObs.trackLaunchReadyHelpClicked(templateId);
  };

  const openFirstFailed = (): void => {
    const firstFailed = categories.find(
      (c) => !c.passed && c.itemType && c.itemId
    );
    if (firstFailed?.itemType && firstFailed.itemId) {
      try {
        onOpenItem(firstFailed.itemType, firstFailed.itemId);
      } catch (err) {
        console.warn('[PreLaunchChecklist] onOpenItem threw', err);
      }
    }
  };

  const progressPct = hasErrors ? Math.round((passedCount / categories.length) * 100) : 100;

  return (
    <Panel data-testid="pre-launch-checklist" role="status" aria-live="polite">
      <Card>
        <GradientStrip />
        <Content>
          <HeaderRow>
            <TitleWrap>
              <Sparkle />
              <Title>Pre-launch checklist</Title>
            </TitleWrap>
            <HeaderActions>
              {hasErrors ? (
                <Pill>
                  {totalErrors} {totalErrors === 1 ? 'item' : 'items'}
                </Pill>
              ) : !allAttested ? (
                <Pill>
                  <InfoPillIcon />
                  {attestRemaining} to review
                </Pill>
              ) : null}
              {onClose && (
                <CloseButton
                  type="button"
                  onClick={onClose}
                  aria-label="Hide checklist"
                  title="Hide — re-runs on Check Changes"
                >
                  <CloseIcon />
                </CloseButton>
              )}
            </HeaderActions>
          </HeaderRow>

          <ProgressRow>
            <ProgressTrack>
              <ProgressFill pct={progressPct} />
            </ProgressTrack>
            <ProgressLabel tone={hasErrors ? 'blue' : allAttested ? 'green' : 'blue'}>
              {hasErrors
                ? `${passedCount} of ${categories.length}`
                : allAttested
                  ? `${categories.length} of ${categories.length} ✓`
                  : 'No errors'}
            </ProgressLabel>
          </ProgressRow>

          {hasErrors ? (
            <RowList>
              {categories.map((c, i) => {
                const clickable = !c.passed && !!c.itemType && !!c.itemId;
                const last = i === categories.length - 1;
                return (
                  <CategoryItem
                    key={c.key}
                    clickable={clickable}
                    last={last}
                    onClick={
                      clickable
                        ? () => onOpenItem(c.itemType!, c.itemId!)
                        : undefined
                    }
                  >
                    {c.passed ? <CheckIcon /> : <InfoIcon />}
                    {c.passed ? (
                      <PassedText>{c.label}</PassedText>
                    ) : (
                      <div>
                        <FailTitle>{c.label}</FailTitle>
                        <FailDetail>
                          {c.itemTitle ? `${c.itemTitle} — ` : ''}
                          {c.detail}
                        </FailDetail>
                      </div>
                    )}
                  </CategoryItem>
                );
              })}
            </RowList>
          ) : allAttested ? (
            <RowList>
              {categories.map((c, i) => (
                <CategoryItem key={c.key} last={i === categories.length - 1}>
                  <CheckIcon />
                  <PassedText>{c.label}</PassedText>
                </CategoryItem>
              ))}
            </RowList>
          ) : (
            <>
              <Eyebrow>Recommended checks</Eyebrow>
              <RowList>
                {ATTESTATIONS.map((a) => {
                  const checked = attestations[a.key];
                  return (
                    <AttestRow key={a.key}>
                      <AttestBox
                        type="button"
                        checked={checked}
                        onClick={() => onToggleAttestation(a.key)}
                        role="checkbox"
                        aria-checked={checked}
                        aria-label={a.title}
                      >
                        {checked && <CheckWhite />}
                      </AttestBox>
                      <AttestTextWrap onClick={() => onToggleAttestation(a.key)}>
                        <AttestTitle>{a.title}</AttestTitle>
                        <AttestDesc>
                          {a.description}
                          <AttestHelp
                            href={a.helpUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Learn more
                          </AttestHelp>
                        </AttestDesc>
                      </AttestTextWrap>
                    </AttestRow>
                  );
                })}
              </RowList>
              <EscapeHatch>
                I can miss some issues, especially for actors and target
                audiences. If you'd like a member of the Dado team to review your
                experience before launching (~3 business days) please contact{' '}
                <MailLink
                  href={`mailto:${HELP_EMAIL}`}
                  onClick={onHelpEmailClick}
                  data-testid="pre-launch-help-link"
                >
                  {HELP_EMAIL}
                </MailLink>
                .
              </EscapeHatch>
            </>
          )}
        </Content>

        <Footer>
          {hasErrors ? (
            <CompleteButton type="button" onClick={openFirstFailed}>
              Complete {totalErrors} {totalErrors === 1 ? 'item' : 'items'}
              <ArrowRight />
            </CompleteButton>
          ) : allAttested ? (
            <ReadyNote>
              <CheckIcon />
              Ready to launch
            </ReadyNote>
          ) : (
            <ReadyNote style={{ color: '#778088', fontWeight: 300 }}>
              Complete the checks above to enable Launch
            </ReadyNote>
          )}
        </Footer>
      </Card>
    </Panel>
  );
};

// Error boundary — render nothing on a caught error so the editor page
// stays alive (same pattern the replaced components used).
interface BoundaryState {
  hasError: boolean;
}

export class PreLaunchChecklistBoundary extends React.Component<
  React.PropsWithChildren<{ fallback?: React.ReactNode }>,
  BoundaryState
> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(err: unknown, info: unknown): void {
    console.warn('[PreLaunchChecklistBoundary] caught render error', { err, info });
  }

  render(): React.ReactNode {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}
